type RouteHandler = () => void | Promise<void>;

export interface LocationSnapshot {
  readonly path: string;
  readonly query: ReadonlyMap<string, string>;
}

function areLocationsEqual(left: LocationSnapshot, right: LocationSnapshot): boolean {
  if (left.path !== right.path || left.query.size !== right.query.size) {
    return false;
  }

  for (const [key, value] of left.query.entries()) {
    if (right.query.get(key) !== value) {
      return false;
    }
  }

  return true;
}

function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      resolve();
    });
  });
}

interface RegisteredRoute {
  readonly path: string;
  readonly handler: RouteHandler;
  readonly paramNames: ReadonlyArray<string>;
  readonly matcher: RegExp;
}

function normalizePath(path: string): string {
  const trimmedPath = path.trim();

  if (trimmedPath === '' || trimmedPath === '#') {
    return '/';
  }

  const withoutHash = trimmedPath.replace(/^#/, '');
  const withLeadingSlash = withoutHash.startsWith('/') ? withoutHash : `/${withoutHash}`;

  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
    return withLeadingSlash.slice(0, -1);
  }

  return withLeadingSlash;
}

function extractLocationParts(pathname: string, search = ''): { path: string; queryString: string } {
  const [rawPath, rawQueryFromPath = ''] = pathname.trim().replace(/^#/, '').split('?');
  const normalizedSearch = search.trim().replace(/^\?/, '');

  return {
    path: rawPath,
    queryString: normalizedSearch || rawQueryFromPath,
  };
}

function createQueryMap(queryString: string): ReadonlyMap<string, string> {
  const searchParams = new URLSearchParams(queryString);
  const query = new Map<string, string>();

  searchParams.forEach((value, key) => {
    if (value.trim() !== '') {
      query.set(key, value);
    }
  });

  return query;
}

export function parseLocation(pathname: string, search = ''): LocationSnapshot {
  const { path, queryString } = extractLocationParts(pathname, search);

  return {
    path: normalizePath(path),
    query: createQueryMap(queryString),
  };
}

export function getCurrentLocation(): LocationSnapshot {
  return parseLocation(window.location.pathname, window.location.search);
}

export function buildRoute(
  path: string,
  query?: Readonly<Record<string, string | undefined>>,
): string {
  const normalizedPath = normalizePath(path);
  const searchParams = new URLSearchParams();

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        searchParams.set(key, value);
      }
    });
  }

  const queryString = searchParams.toString();

  return `${normalizedPath}${queryString ? `?${queryString}` : ''}`;
}

function translateLegacyHashLocation(): void {
  const { hash } = window.location;

  if (!hash.startsWith('#/')) {
    return;
  }

  const legacySnapshot = parseLocation(hash.slice(1));
  const legacyQuery = Object.fromEntries(legacySnapshot.query.entries());
  const canonicalRoute = buildRoute(legacySnapshot.path, legacyQuery);
  const currentRoute = buildRoute(window.location.pathname, Object.fromEntries(getCurrentLocation().query.entries()));

  if (canonicalRoute === currentRoute && hash !== '') {
    window.history.replaceState(null, '', canonicalRoute);
    return;
  }

  window.history.replaceState(null, '', canonicalRoute);
}

function escapeRegExp(segment: string): string {
  return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function compilePath(path: string): Pick<RegisteredRoute, 'matcher' | 'paramNames'> {
  const normalizedPath = normalizePath(path);
  const segments = normalizedPath.split('/').filter(Boolean);
  const paramNames: string[] = [];

  if (segments.length === 0) {
    return {
      matcher: /^\/$/,
      paramNames,
    };
  }

  const pattern = segments
    .map((segment) => {
      if (segment.startsWith(':')) {
        paramNames.push(segment.slice(1));
        return '([^/]+)';
      }

      return escapeRegExp(segment);
    })
    .join('/');

  return {
    matcher: new RegExp(`^/${pattern}$`),
    paramNames,
  };
}

export class Router {
  private readonly routes: RegisteredRoute[] = [];

  private readonly params = new Map<string, string>();

  private readonly query = new Map<string, string>();

  private fallbackHandler: RouteHandler | null = null;

  private resolutionSequence = 0;

  private readonly handlePopState = () => {
    void this.resolve();
  };

  private readonly handleDocumentClick = (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const anchor = event
      .composedPath()
      .find((target): target is HTMLAnchorElement => target instanceof HTMLAnchorElement);

    if (!anchor) {
      return;
    }

    const href = anchor.getAttribute('href');

    if (
      !href ||
      href.startsWith('#') ||
      anchor.hasAttribute('download') ||
      anchor.target === '_blank'
    ) {
      return;
    }

    const targetUrl = new URL(anchor.href, window.location.origin);

    if (targetUrl.origin !== window.location.origin) {
      return;
    }

    event.preventDefault();
    this.navigate(targetUrl.pathname, Object.fromEntries(targetUrl.searchParams.entries()));
  };

  register(path: string, handler: RouteHandler): void {
    const normalizedPath = normalizePath(path);
    const { matcher, paramNames } = compilePath(normalizedPath);

    this.routes.push({
      path: normalizedPath,
      handler,
      paramNames,
      matcher,
    });
  }

  setFallback(handler: RouteHandler): void {
    this.fallbackHandler = handler;
  }

  start(): void {
    translateLegacyHashLocation();
    window.addEventListener('popstate', this.handlePopState);
    document.addEventListener('click', this.handleDocumentClick);

    void this.resolve();
  }

  navigate(path: string, query?: Readonly<Record<string, string | undefined>>): void {
    const targetRoute = buildRoute(path, query);
    const currentRoute = buildRoute(window.location.pathname, Object.fromEntries(getCurrentLocation().query.entries()));

    if (currentRoute === targetRoute) {
      void this.resolve();
      return;
    }

    window.history.pushState(null, '', targetRoute);
    void this.resolve();
  }

  getParam(name: string): string | null {
    return this.params.get(name) ?? null;
  }

  getQueryParam(name: string): string | null {
    return this.query.get(name) ?? null;
  }

  getQueryParams(): ReadonlyMap<string, string> {
    return new Map(this.query);
  }

  private async resolve(): Promise<void> {
    const resolutionId = ++this.resolutionSequence;
    const currentLocation = getCurrentLocation();
    const currentPath = currentLocation.path;

    for (const route of this.routes) {
      const matches = currentPath.match(route.matcher);

      if (!matches) {
        continue;
      }

      this.params.clear();
      this.query.clear();

      route.paramNames.forEach((paramName, index) => {
        const matchValue = matches[index + 1];

        if (matchValue) {
          this.params.set(paramName, decodeURIComponent(matchValue));
        }
      });

      currentLocation.query.forEach((value, key) => {
        this.query.set(key, value);
      });

      await route.handler();
      return;
    }

    this.params.clear();
    this.query.clear();

    if (this.fallbackHandler) {
      await waitForNextFrame();

      if (resolutionId !== this.resolutionSequence) {
        return;
      }

      const latestLocation = getCurrentLocation();

      if (!areLocationsEqual(currentLocation, latestLocation)) {
        return;
      }

      await this.fallbackHandler();
    }
  }
}
